"use client"
import { useEffect } from "react"
import { useSessionContext } from "@workspace/livekit/react"
import { useMeridialStore } from "@/store"
import { useRemoteControl } from "@/hooks/use-remote-control"
import { useToolRpc } from "@/hooks/use-tool-rpc"
import { streamTools } from "@/lib/stream-tools"
import { AnnotationOverlay } from "@/components/annotation-overlay"
import { RemoteCursor } from "@/components/remote-cursor"
/**
 * Renders the cobrowsing layer (support cursors + screen annotations) and wires
 * up client-side tool execution. Lives inside the session provider so it keeps
 * working across the chat and live views, and after a human support handoff.
 */
export function CobrowseLayer() {
  const { room } = useSessionContext()
  const voiceboxProps = useMeridialStore((s) => s.props)
  const tools = voiceboxProps?.tools
  const toolCallbacks = voiceboxProps?.toolCallbacks
  const remoteControl = useRemoteControl({ room })
  // Register the tool_call RPC handler before streaming the schema, so the agent
  // can never call a tool the client isn't yet ready to handle.
  useToolRpc({
    room,
    tools,
    onToolCall: (event) => toolCallbacks?.[event.toolId]?.(event),
  })

  // Stream the tools schema to the python livekit agent.
  useEffect(() => {
    if (!tools || room.state !== "connected") return
    const entries = Object.entries(tools)
    if (entries.length > 15) {
      console.warn(
        `[Meridial] ${entries.length} tools registered; only the first 15 are sent to the agent.`
      )
    }
    const toolsSchema = entries.slice(0, 15).map(([name, definition]) => ({
      type: "function",
      name,
      description: definition.description,
      parameters: definition.inputSchema.toJSONSchema(),
    }))
    streamTools(room, toolsSchema)
  }, [tools, room, room.state])

  return (
    <>
      <AnnotationOverlay strokes={remoteControl.annotations} />
      {remoteControl.cursors.map((cursor) => (
        <RemoteCursor
          key={cursor.id}
          x={cursor.x}
          y={cursor.y}
          name={cursor.name}
        />
      ))}
    </>
  )
}
